import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveMedia } from "../lib/api";
import { ArrowLeft } from "lucide-react";
import { InkByName, InkFlourish } from "../components/Ink";
import { useI18n } from "../i18n";

export default function Article() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [translating, setTranslating] = useState(false);
  const { lang } = useI18n();

  useEffect(() => {
    api.get(`/articles/${id}`).then((r) => setArticle(r.data)).catch(() => {});
  }, [id]);

  // When toggled to EN, make sure this article's cached EN fields are loaded.
  useEffect(() => {
    if (!article || lang !== "en") return;
    if (article.title_en && article.body_en) return;
    (async () => {
      setTranslating(true);
      try {
        const r = await api.post(`/articles/${article.id}/translate?lang=en`);
        setArticle(r.data);
      } catch (e) {
        // DOM-walker will still handle it as a fallback.
      } finally {
        setTranslating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.id, lang]);

  if (!article) return <div className="max-w-3xl mx-auto px-6 py-24 text-subtle">Cargando…</div>;

  // Choose each field by current language, with safe fallbacks.
  const t = (field) => {
    if (lang === "en") {
      const en = article[`${field}_en`];
      if (en) return en;
    }
    return article[field] || "";
  };
  const dateLocale = lang === "en" ? "en-US" : "es-MX";
  const byLabel = lang === "en" ? "By" : "Por";
  const backLabel = lang === "en" ? "Back to Journal" : "Volver al Journal";
  const body = t("body");
  const isHtml = /<[a-z][\s\S]*>/i.test(body || "");

  return (
    <article className="pb-24 bg-white" data-testid="article-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-10">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-subtle hover:text-cobalt font-display font-semibold">
          <ArrowLeft size={14} /> {backLabel}
        </Link>
      </div>
      <header className="max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-10 text-center">
        {article.ink_mark ? (
          <InkByName name={article.ink_mark} className="w-20 h-16 mx-auto opacity-80" />
        ) : (
          <InkFlourish className="w-24 h-5 mx-auto opacity-60" />
        )}
        <p className="eyebrow mt-6" data-no-translate={lang === "en" && t("category_en") ? "true" : "false"}>{t("category")}</p>
        <h1
          className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink"
          data-no-translate={lang === "en" && article.title_en ? "true" : "false"}
        >
          {t("title")}
        </h1>
        <p
          className="mt-6 text-lg sm:text-xl text-muted2 leading-relaxed font-light"
          data-no-translate={lang === "en" && article.dek_en ? "true" : "false"}
        >
          {t("dek")}
        </p>
        <div className="mt-8 flex items-center justify-center gap-4 text-xs tracking-[0.22em] uppercase text-subtle font-display font-semibold">
          <span>{byLabel} {article.author}</span>
          <span className="w-8 h-px bg-cobalt/40" />
          <span data-no-translate="true">{new Date(article.published_at).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</span>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <img src={resolveMedia(article.image)} alt={t("title")} className="w-full h-[420px] sm:h-[600px] object-cover" />
      </div>
      <div
        className="max-w-3xl mx-auto px-6 sm:px-10 mt-12 prose-editorial"
        data-no-translate={lang === "en" && article.body_en ? "true" : "false"}
      >
        {translating && lang === "en" && !article.body_en ? (
          <p className="text-subtle italic">Translating…</p>
        ) : isHtml ? (
          <div dangerouslySetInnerHTML={{ __html: body }} data-testid="article-body-html" />
        ) : (
          (body || "").split("\n").map((p, i) => <p key={i}>{p}</p>)
        )}
      </div>
      <div className="max-w-3xl mx-auto px-6 sm:px-10 mt-16 text-center">
        {article.ink_mark && <InkByName name={article.ink_mark} className="w-16 h-14 mx-auto opacity-60" />}
      </div>
    </article>
  );
}
